import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Snackbar, Alert, Paper, Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import AdminPageHeader  from "../../components/admin/AdminPageHeader";
import AdminGridToolbar from "../../components/admin/AdminGridToolbar";
import AdminFormDialog  from "../../components/admin/AdminFormDialog";
import {
  listTermsVersions, uploadTermsVersion, updateTermsVersion, deleteTermsVersion,
} from "../../services/adminService";
import { termsFileUrl } from "../../services/termsService";

const EMPTY = { version: "", effective_date: "" };

export default function TermsVersionPage() {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [rowSel,    setRowSel]    = useState({ type: "include", ids: new Set() });
  const [dialogOpen,setDialogOpen]= useState(false);
  const [deleteOpen,setDeleteOpen]= useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [file,      setFile]      = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [snack,     setSnack]     = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSel.ids), [rowSel]);
  const selectedRow = useMemo(() => rows.find(r => r.id === selectedIds[0]), [rows, selectedIds]);

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTermsVersions();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { showSnack(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFile(null); setDialogOpen(true); };

  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({
      version: selectedRow.version,
      effective_date: selectedRow.effective_date ? String(selectedRow.effective_date).slice(0, 10) : "",
    });
    setFile(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.effective_date) return showSnack("Data de vigência é obrigatória.", "error");
    if (file && file.type !== "application/pdf") return showSnack("O arquivo precisa ser um PDF.", "error");
    if (!editing && !form.version.trim()) return showSnack("Versão é obrigatória.", "error");
    if (!editing && !file)                return showSnack("Selecione o arquivo PDF.", "error");

    setSaving(true);
    try {
      if (editing) {
        await updateTermsVersion(editing.id, form.effective_date, file);
        showSnack("Versão atualizada.");
      } else {
        await uploadTermsVersion(file, form.version.trim(), form.effective_date);
        showSnack("Versão cadastrada.");
      }
      setDialogOpen(false);
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => deleteTermsVersion(id)));
      showSnack("Versão(ões) removida(s).");
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch { showSnack("Erro ao remover.", "error"); }
    finally { setSaving(false); }
  };

  const columns = [
    { field: "version", headerName: "Versão", width: 120 },
    {
      field: "effective_date", headerName: "Vigente a partir de", width: 160,
      valueGetter: (v) => v ? new Date(v).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "",
    },
    { field: "file_name", headerName: "Arquivo", flex: 1 },
    {
      field: "created_at", headerName: "Cadastrada em", width: 160,
      valueGetter: (v) => v ? new Date(v).toLocaleDateString("pt-BR") : "",
    },
    {
      field: "__view", headerName: "", width: 90, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          href={termsFileUrl(row.id)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "none", fontSize: 12 }}
        >
          Ver PDF
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <AdminPageHeader title="Termos de Uso" />

      <Paper sx={{ borderRadius: 0, border: "1px solid", borderColor: "divider" }}>
        <DataGrid
          rows={rows} columns={columns} getRowId={r => r.id}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSel}
          onRowSelectionModelChange={m => setRowSel({ type: "include", ids: new Set(Array.from(m?.ids ?? [])) })}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: "Versões",
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
        />
      </Paper>

      <AdminFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSave} saving={saving}
        title={editing ? "Editar Versão dos Termos de Uso" : "Nova Versão dos Termos de Uso"}>

        <TextField label="Versão *" size="small" InputLabelProps={{ shrink: true }}
          placeholder="ex: 1.1"
          disabled={!!editing}
          helperText={editing ? "O número da versão não muda ao editar." : ""}
          value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))} />

        <TextField label="Data de vigência *" type="date" size="small" InputLabelProps={{ shrink: true }}
          value={form.effective_date} onChange={e => setForm(p => ({ ...p, effective_date: e.target.value }))} />

        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          sx={{ textTransform: "none", justifyContent: "flex-start", fontSize: 13 }}
        >
          {file ? file.name : editing ? "Substituir arquivo PDF (opcional)" : "Selecionar arquivo PDF"}
          <input
            type="file"
            accept="application/pdf"
            hidden
            onChange={e => setFile(e.target.files?.[0] ?? null)}
          />
        </Button>
      </AdminFormDialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: 0 } } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1 }}>Remover versão</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {selectedIds.length > 1
              ? `Remover ${selectedIds.length} versões?`
              : `Remover a versão selecionada?`}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>Cancelar</Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f" }}>
            {saving ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
